import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Manga, KnownSite, TierGrade, MangaSource, TIER_ORDER } from './types/manga';
import {
  getLocalManga,
  saveLocalManga,
  getKnownSites,
  saveKnownSites,
  sanitizeMangaList,
  getMangaFromIndexedDB,
  mergeMangaLists,
  getLocalMangaTimestamp,
  syncToCloudflare,
  saveUserBackup,
  getUserBackup,
  fetchCloudflareBackup,
  downloadBackupFile,
  importBackupJSON,
  UserBackupData,
  DEFAULT_KNOWN_SITES,
  recordDeletedMangaId,
  unrecordDeletedMangaId,
} from './services/storage';
import {
  checkSourceChapter,
  autoDiscoverMangaOnSite,
  checkMangaSources,
  autoDiscoverMangaSources,
  preloadRecentUpdatesFeed,
  findMangaWithFeedUpdates,
} from './services/scraper';
import { INITIAL_MANGA_LIST } from './data/initialData';
import { isUpdatedToday, getTodayKey, getSourceName } from './utils/helpers';
import { Header } from './components/Header';
import { TierBoard } from './components/TierBoard';
import { MangaModal } from './components/MangaModal';
import { UpdatedTodayModal } from './components/UpdatedTodayModal';
import { DownloadLibraryModal } from './components/DownloadLibraryModal';
import { SiteManagerModal } from './components/SiteManagerModal';
import { SourceDetailsModal } from './components/SourceDetailsModal';
import { MobileBottomNav } from './components/MobileBottomNav';
import { DiscoverPage } from './components/DiscoverPage';
import { RealtimeSyncStatus } from './components/RealtimeStatus';
import { Loader2, CheckCircle2, AlertCircle, Check, Info } from 'lucide-react';

/** Same object when the order is unchanged, so cards that didn't move don't re-render */
const withOrder = (m: Manga, order: number): Manga => (m.order === order ? m : { ...m, order });

/** A checker result together with the manga as it was when the check read it */
interface CheckedManga {
  base: Manga;
  updated: Manga;
}

/** The fields a check reads and rewrites; if these changed meanwhile, the result is stale */
const chapterTrackingKey = (m: Manga) =>
  JSON.stringify([Number(m.currentCh) || 0, (m.sources || []).map((s) => [s.url, s.chapter || 0])]);

const REALTIME_POLL_MS = 3 * 60 * 1000;
const FULL_CHECK_INTERVAL_MS = 30 * 60 * 1000;
// Bump the suffix when chapter-check rules change: every device then runs a full check on next open
// instead of waiting out the 30 minutes since its last one (v2: chapter = each site's top chapter)
const LAST_FULL_CHECK_KEY = 'manga_last_full_check_at_v2';

export const App: React.FC = () => {
  const [mangaList, setMangaList] = useState<Manga[]>([]);
  const [sites, setSites] = useState<KnownSite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [backupInfo, setBackupInfo] = useState<UserBackupData | null>(() => getUserBackup());

  // Search & Sorting
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [isSortMode, setIsSortMode] = useState(false);

  // Modals state
  const [isMangaModalOpen, setIsMangaModalOpen] = useState(false);
  const [editingManga, setEditingManga] = useState<Manga | null>(null);
  const [isUpdatedTodayOpen, setIsUpdatedTodayOpen] = useState(false);
  const [isDownloadLibraryOpen, setIsDownloadLibraryOpen] = useState(false);
  const [isSiteManagerOpen, setIsSiteManagerOpen] = useState(false);
  const [sourceDetailsManga, setSourceDetailsManga] = useState<Manga | null>(null);

  // Realtime chapter tracking (replaces the manual "check chapters" button)
  const [syncStatus, setSyncStatus] = useState<RealtimeSyncStatus>({
    phase: 'idle',
    lastSyncAt: null,
    progress: null,
  });
  const syncRunningRef = useRef(false);
  const mangaListRef = useRef<Manga[]>([]);
  mangaListRef.current = mangaList;
  const [view, setView] = useState<'tier' | 'discover'>('tier');

  // Re-render once a minute so "อัปล่าสุด X นาทีที่แล้ว" on the cards stays current
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setClockTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const [isAutoDiscoveringAll, setIsAutoDiscoveringAll] = useState(false);
  const [autoDiscoverProgress, setAutoDiscoverProgress] = useState<{
    current: number;
    total: number;
    title: string;
    foundCount: number;
    remainingSeconds: number | null;
  } | null>(null);
  const abortAutoDiscoverRef = useRef(false);

  const [toastMessage, setToastMessage] = useState<{
    type: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);

  const showToast = (text: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage({ text, type });
    setTimeout(() => {
      setToastMessage(null);
    }, 4500);
  };

  // Initial load: Instant paint from localStorage (v3), resilient check from IndexedDB, and smart merge with remote backend
  useEffect(() => {
    let isMounted = true;

    async function initData() {
      try {
        const localSites = getKnownSites();
        if (isMounted) setSites(localSites);

        // 1. Instant paint from LocalStorage
        let currentList = getLocalManga();
        if (isMounted) setMangaList(currentList);

        // 2. Check IndexedDB (resilient storage of truth)
        const idbManga = await getMangaFromIndexedDB();
        if (idbManga && idbManga.length > 0) {
          currentList = mergeMangaLists(currentList, idbManga);
          if (isMounted) setMangaList(currentList);
        }

        // 3. Fetch remote from Cloudflare KV
        try {
          const res = await fetch('/api/manga', { cache: 'no-store' });
          if (res.ok) {
            const remotePayload = await res.json();
            let remoteList: Manga[] = [];
            let remoteUpdatedAt = 0;

            if (Array.isArray(remotePayload)) {
              remoteList = remotePayload;
            } else if (remotePayload && Array.isArray(remotePayload.mangaList)) {
              remoteList = remotePayload.mangaList;
              remoteUpdatedAt = Number(remotePayload.updatedAt) || 0;
            }

            if (remoteList.length > 0) {
              const localTimestamp = getLocalMangaTimestamp();
              const localSourceCount = currentList.reduce((acc, m) => acc + (m.sources?.length || 0), 0);
              const remoteSourceCount = remoteList.reduce((acc, m) => acc + (m.sources?.length || 0), 0);

              if (localTimestamp === 0 || remoteUpdatedAt > localTimestamp || remoteSourceCount > localSourceCount) {
                const isRemoteNewer = localTimestamp === 0 || remoteUpdatedAt > localTimestamp;
                const merged = mergeMangaLists(currentList, remoteList, isRemoteNewer);
                if (isMounted) setMangaList(merged);

                const hasLocalNewManga = currentList.some(
                  (loc) => !remoteList.some((rem) => rem.id === loc.id || rem.title?.trim().toLowerCase() === loc.title?.trim().toLowerCase())
                );

                if (hasLocalNewManga) {
                  saveLocalManga(merged, Date.now(), true);
                } else if (isRemoteNewer) {
                  saveLocalManga(merged, remoteUpdatedAt, false);
                } else {
                  saveLocalManga(merged, localTimestamp, true);
                }
              } else if (localTimestamp > remoteUpdatedAt) {
                syncToCloudflare(currentList, localTimestamp).catch(() => {});
              }
            }
          }
        } catch (fetchErr) {
          console.warn('Remote sync fetch error (offline mode):', fetchErr);
        }

        // 4. Fetch Cloudflare backup info
        fetchCloudflareBackup()
          .then((b) => {
            if (b && isMounted) setBackupInfo(b);
          })
          .catch(() => {});
      } catch (err) {
        console.error('Initialization error:', err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    initData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Auto-sync from cloud when tab becomes active / window focused (e.g. switching between PC and Phone)
  useEffect(() => {
    const handleSync = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await fetch('/api/manga', { cache: 'no-store' });
        if (!res.ok) return;
        const remotePayload = await res.json();
        if (!remotePayload) return;

        let remoteList: Manga[] = [];
        let remoteUpdatedAt = 0;

        if (Array.isArray(remotePayload)) {
          remoteList = remotePayload;
        } else if (remotePayload && Array.isArray(remotePayload.mangaList)) {
          remoteList = remotePayload.mangaList;
          remoteUpdatedAt = Number(remotePayload.updatedAt) || 0;
        }

        if (remoteList.length === 0) return;
        const localTimestamp = getLocalMangaTimestamp();

        setMangaList((prev) => {
          const localSourceCount = prev.reduce((acc, m) => acc + (m.sources?.length || 0), 0);
          const remoteSourceCount = remoteList.reduce((acc, m) => acc + (m.sources?.length || 0), 0);
          if (remoteUpdatedAt > localTimestamp || remoteSourceCount > localSourceCount) {
            const isRemoteNewer = remoteUpdatedAt > localTimestamp;
            const merged = mergeMangaLists(prev, remoteList, isRemoteNewer);
            const hasLocalNewManga = prev.some(
              (loc) => !remoteList.some((rem) => rem.id === loc.id || rem.title?.trim().toLowerCase() === loc.title?.trim().toLowerCase())
            );
            if (hasLocalNewManga) {
              saveLocalManga(merged, Date.now(), true);
            } else if (isRemoteNewer) {
              saveLocalManga(merged, remoteUpdatedAt, false);
            } else {
              saveLocalManga(merged, localTimestamp, true);
            }
            return merged;
          }
          return prev;
        });
      } catch {}
    };

    window.addEventListener('visibilitychange', handleSync);
    window.addEventListener('focus', handleSync);
    return () => {
      window.removeEventListener('visibilitychange', handleSync);
      window.removeEventListener('focus', handleSync);
    };
  }, []);

  // Sync to local storage & Cloudflare on changes
  const updateMangaState = useCallback((newList: Manga[]) => {
    setMangaList(newList);
    saveLocalManga(newList);
  }, []);

  // Quick +1 chapter
  const handleQuickPlus = (id: string) => {
    setMangaList((prev) => {
      const updated = prev.map((m) => {
        if (m.id === id) {
          return { ...m, readCh: (Number(m.readCh) || 0) + 1 };
        }
        return m;
      });
      saveLocalManga(updated);
      return updated;
    });
  };

  // Quick -1 chapter
  const handleQuickMinus = (id: string) => {
    setMangaList((prev) => {
      const updated = prev.map((m) => {
        if (m.id === id && Number(m.readCh) > 0) {
          return { ...m, readCh: Number(m.readCh) - 1 };
        }
        return m;
      });
      saveLocalManga(updated);
      return updated;
    });
  };

  // Open Edit Modal
  const handleEdit = (manga: Manga) => {
    setEditingManga(manga);
    setIsMangaModalOpen(true);
  };

  // Save (Create or Update)
  const handleSaveManga = (manga: Manga) => {
    const existingIndex = mangaList.findIndex((m) => m.id === manga.id);
    let updated: Manga[];

    if (existingIndex !== -1) {
      const oldManga = mangaList[existingIndex];
      if (oldManga.grade !== manga.grade) {
        // Tier changed during edit: move to the VERY END of the new tier
        const listWithout = mangaList.filter((m) => m.id !== manga.id);
        const result: Manga[] = [];
        for (const g of TIER_ORDER) {
          const items = listWithout
            .filter((m) => m.grade === g)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          if (g === manga.grade) {
            items.push(manga);
          }
          result.push(...items);
        }
        updated = result;
      } else {
        // Grade unchanged: preserve order and update fields
        updated = mangaList.map((m) => (m.id === manga.id ? manga : m));
      }
      showToast(`บันทึกข้อมูล "${manga.title}" เรียบร้อยแล้ว`);
    } else {
      // New manga: unconditionally place at the VERY END of its tier
      const result: Manga[] = [];
      for (const g of TIER_ORDER) {
        const items = mangaList
          .filter((m) => m.grade === g)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        if (g === manga.grade) {
          items.push(manga);
        }
        result.push(...items);
      }
      updated = result;
      showToast(`เพิ่มมังงะเรื่องใหม่ "${manga.title}" สำเร็จ!`);
    }

    // Remove from deleted tombstones if previously recorded
    unrecordDeletedMangaId(manga.id);

    // Re-index global order continuously: S -> A+ -> A -> B -> C -> EX
    let globalOrder = 1;
    const finalOrderedList = updated.map((m) => withOrder(m, globalOrder++));

    updateMangaState(finalOrderedList);
  };

  // Delete
  const handleDeleteManga = (id: string) => {
    const target = mangaList.find((m) => m.id === id);
    const updated = mangaList.filter((m) => m.id !== id);
    recordDeletedMangaId(id);
    let globalOrder = 1;
    const finalOrderedList = updated.map((m) => withOrder(m, globalOrder++));
    updateMangaState(finalOrderedList);
    showToast(`ลบ "${target?.title || 'มังงะ'}" ออกจากรายการแล้ว`, 'info');
  };

  // Drag and drop reordering
  const handleReorder = (draggedId: string, targetId: string) => {
    if (draggedId === targetId) return;

    const draggedManga = mangaList.find((m) => m.id === draggedId);
    const targetManga = mangaList.find((m) => m.id === targetId);
    if (!draggedManga || !targetManga) return;

    const targetGrade = targetManga.grade;
    const listWithout = mangaList.filter((m) => m.id !== draggedId);
    const updatedDragged = { ...draggedManga, grade: targetGrade };

    const targetIdx = listWithout.findIndex((m) => m.id === targetId);
    if (targetIdx === -1) return;

    listWithout.splice(targetIdx, 0, updatedDragged);

    let globalOrder = 1;
    const reordered = listWithout.map((item) => withOrder(item, globalOrder++));

    updateMangaState(reordered);
  };

  // Quick Move Up on mobile (within tier)
  const handleMoveUp = (id: string) => {
    const manga = mangaList.find((m) => m.id === id);
    if (!manga) return;
    const tierManga = mangaList
      .filter((m) => m.grade === manga.grade)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const tierIdx = tierManga.findIndex((m) => m.id === id);
    if (tierIdx <= 0) return;

    const prevManga = tierManga[tierIdx - 1];
    const globalIdx = mangaList.findIndex((m) => m.id === id);
    const prevGlobalIdx = mangaList.findIndex((m) => m.id === prevManga.id);

    const list = [...mangaList];
    list[globalIdx] = prevManga;
    list[prevGlobalIdx] = manga;

    let globalOrder = 1;
    const reordered = list.map((item) => withOrder(item, globalOrder++));
    updateMangaState(reordered);
  };

  // Quick Move Down on mobile (within tier)
  const handleMoveDown = (id: string) => {
    const manga = mangaList.find((m) => m.id === id);
    if (!manga) return;
    const tierManga = mangaList
      .filter((m) => m.grade === manga.grade)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const tierIdx = tierManga.findIndex((m) => m.id === id);
    if (tierIdx === -1 || tierIdx >= tierManga.length - 1) return;

    const nextManga = tierManga[tierIdx + 1];
    const globalIdx = mangaList.findIndex((m) => m.id === id);
    const nextGlobalIdx = mangaList.findIndex((m) => m.id === nextManga.id);

    const list = [...mangaList];
    list[globalIdx] = nextManga;
    list[nextGlobalIdx] = manga;

    let globalOrder = 1;
    const reordered = list.map((item) => withOrder(item, globalOrder++));
    updateMangaState(reordered);
  };

  // Move to tier (places at the end of that tier)
  const handleMoveToTier = (mangaId: string, targetGrade: TierGrade) => {
    const manga = mangaList.find((m) => m.id === mangaId);
    if (!manga) return;

    const listWithout = mangaList.filter((m) => m.id !== mangaId);
    const updatedManga = { ...manga, grade: targetGrade };
    const result: Manga[] = [];

    for (const g of TIER_ORDER) {
      const items = listWithout
        .filter((m) => m.grade === g)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      if (g === targetGrade) {
        items.push(updatedManga);
      }
      result.push(...items);
    }

    let globalOrder = 1;
    const reordered = result.map((item) => withOrder(item, globalOrder++));

    updateMangaState(reordered);
  };

  // Save sites
  const handleSaveSites = (newSites: KnownSite[]) => {
    setSites(newSites);
    saveKnownSites(newSites);
    showToast('บันทึกรายชื่อเว็บสำหรับตรวจเช็คเรียบร้อย');
  };

  // Reset manga sources/links to baseline while preserving reading progress (readCh, currentCh), user ranks (grade, order), and newly added manga
  const handleResetToDefault = () => {
    const existingById = new Map<string, Manga>();
    const existingByTitle = new Map<string, Manga>();
    for (const m of mangaList) {
      if (m.id) existingById.set(m.id, m);
      if (m.title) existingByTitle.set(m.title.trim().toLowerCase(), m);
    }

    const matchedExistingIds = new Set<string>();

    const mergedList: Manga[] = INITIAL_MANGA_LIST.map((baseManga) => {
      const existing = existingById.get(baseManga.id) || existingByTitle.get(baseManga.title.trim().toLowerCase());
      if (existing) {
        matchedExistingIds.add(existing.id);

        // Keep baseline sources but retain latest detected chapter numbers if available
        const preservedSources = baseManga.sources.map((bs) => {
          const matchingExistingSource = existing.sources?.find(
            (es) => es.name === bs.name || es.url.replace(/\/$/, '') === bs.url.replace(/\/$/, '')
          );
          return {
            ...bs,
            chapter: matchingExistingSource?.chapter ?? bs.chapter,
          };
        });

        const hasValidExistingLatest = preservedSources.some(
          (s) => s.url.replace(/\/$/, '') === existing.latestSourceUrl?.replace(/\/$/, '')
        );

        return {
          ...baseManga,
          grade: existing.grade || baseManga.grade,
          order: existing.order ?? baseManga.order,
          currentCh: existing.currentCh ?? baseManga.currentCh,
          readCh: existing.readCh ?? baseManga.readCh,
          lastUpdatedAt: existing.lastUpdatedAt || baseManga.lastUpdatedAt,
          sources: preservedSources,
          latestSourceUrl: hasValidExistingLatest
            ? existing.latestSourceUrl
            : baseManga.latestSourceUrl || preservedSources[0]?.url || '',
          latestSourceName: hasValidExistingLatest
            ? existing.latestSourceName
            : baseManga.latestSourceName || preservedSources[0]?.name || '',
        };
      }
      return { ...baseManga };
    });

    // Retain any newly added manga not in INITIAL_MANGA_LIST
    for (const m of mangaList) {
      if (!matchedExistingIds.has(m.id)) {
        mergedList.push(m);
      }
    }

    // Canonicalize ordering
    const cleanList = sanitizeMangaList(mergedList);
    updateMangaState(cleanList);
    setSites(DEFAULT_KNOWN_SITES);
    saveKnownSites(DEFAULT_KNOWN_SITES);
    showToast('รีเซ็ตแหล่งข้อมูลเว็บเป็นค่าเริ่มต้นสำเร็จ (คงจำนวนตอนและแรงค์ไว้ครบถ้วน)', 'success');
  };

  // User Snapshot Backup & Restore
  const handleSaveBackup = () => {
    const saved = saveUserBackup(mangaList, sites);
    setBackupInfo(saved);
    showToast(`บันทึกจุดสำรองข้อมูลเรียบร้อยแล้ว (${saved.count} เรื่อง)`, 'success');
  };

  const handleRestoreBackup = () => {
    const backup = getUserBackup() || backupInfo;
    if (!backup || !backup.mangaList) {
      showToast('ไม่พบข้อมูลจุดสำรองสำหรับกู้คืน', 'error');
      return;
    }
    const cleanList = sanitizeMangaList(backup.mangaList);
    updateMangaState(cleanList);
    if (backup.sites && backup.sites.length > 0) {
      setSites(backup.sites);
      saveKnownSites(backup.sites);
    }
    showToast(`กู้คืนข้อมูลจากจุดสำรองสำเร็จ (${cleanList.length} เรื่อง)`, 'success');
  };

  const handleExportBackup = () => {
    downloadBackupFile(mangaList, sites);
    showToast('ดาวน์โหลดไฟล์สำรองเรียบร้อย', 'info');
  };

  const handleImportBackup = async (file: File) => {
    try {
      const text = await file.text();
      const parsed = importBackupJSON(text);
      const cleanList = sanitizeMangaList(parsed.mangaList);
      updateMangaState(cleanList);
      if (parsed.sites && parsed.sites.length > 0) {
        setSites(parsed.sites);
        saveKnownSites(parsed.sites);
      }
      const newBackup = saveUserBackup(cleanList, parsed.sites);
      setBackupInfo(newBackup);
      showToast(`นำเข้าข้อมูลจากไฟล์สำเร็จ (${cleanList.length} เรื่อง)`, 'success');
    } catch (e: any) {
      showToast(`นำเข้าไฟล์ล้มเหลว: ${e.message}`, 'error');
    }
  };

  // Merge checker results into the CURRENT list by id. Only chapter-tracking fields are taken, so
  // edits the user makes while a background check runs (readCh, grade, title...) are never lost.
  // A result whose manga had its sources or chapter edited since the check read it (`base`) is
  // dropped instead, otherwise it would bring back a deleted source; the next check picks it up.
  const applyCheckedManga = useCallback((checked: CheckedManga[], syncCloud: boolean) => {
    // An empty final flush still syncs the batches applied before it
    if (checked.length === 0 && !syncCloud) return;
    const byId = new Map(checked.map((c) => [c.updated.id, c]));
    setMangaList((prev) => {
      const next = prev.map((m) => {
        const entry = byId.get(m.id);
        if (!entry || chapterTrackingKey(m) !== chapterTrackingKey(entry.base)) return m;
        const c = entry.updated;
        return {
          ...m,
          currentCh: c.currentCh,
          sources: c.sources,
          lastUpdatedAt: c.lastUpdatedAt,
          latestChapterAt: c.latestChapterAt,
          latestSourceUrl: c.latestSourceUrl,
          latestSourceName: c.latestSourceName,
        };
      });
      saveLocalManga(next, Date.now(), syncCloud);
      return next;
    });
  }, []);

  const announceNewChapters = (updated: Manga[]) => {
    if (updated.length === 0) return;
    const names = updated
      .slice(0, 3)
      .map((m) => `${m.title} ตอน ${m.currentCh}`)
      .join(', ');
    const more = updated.length > 3 ? ` และอีก ${updated.length - 3} เรื่อง` : '';
    showToast(`📢 ตอนใหม่: ${names}${more}`, 'success');
  };

  // Full check of every manga (worker pool). Runs in the background on open and every 30 minutes.
  const runFullCheck = useCallback(async () => {
    if (syncRunningRef.current) return;
    const workingList = mangaListRef.current;
    if (workingList.length === 0) return;
    syncRunningRef.current = true;
    setSyncStatus((s) => ({ ...s, phase: 'checking', progress: { current: 0, total: workingList.length } }));

    const newChapterManga: Manga[] = [];
    let pending: CheckedManga[] = [];
    let anyChanges = false;
    try {
      await preloadRecentUpdatesFeed(true);

      let nextIndex = 0;
      let completedCount = 0;
      const CONCURRENCY = 10;
      const workers = Array.from({ length: Math.min(CONCURRENCY, workingList.length) }, async () => {
        while (nextIndex < workingList.length) {
          const queued = workingList[nextIndex++];
          // Check the latest version, so edits made since the run started are what gets checked
          const manga = mangaListRef.current.find((m) => m.id === queued.id);
          if (!manga) {
            completedCount++;
            continue;
          }
          try {
            const { updatedManga, hasUpdate, hasChanges } = await checkMangaSources(manga);
            if (hasUpdate) newChapterManga.push(updatedManga);
            if (hasChanges) {
              pending.push({ base: manga, updated: updatedManga });
              anyChanges = true;
            }
          } catch {
            // Ignore single manga error
          }
          completedCount++;
          if (pending.length >= 5) {
            applyCheckedManga(pending, false);
            pending = [];
          }
          setSyncStatus((s) => ({ ...s, progress: { current: completedCount, total: workingList.length } }));
        }
      });
      await Promise.all(workers);

      // Final flush syncs to Cloudflare once, instead of once per batch
      if (anyChanges) applyCheckedManga(pending, true);
      try {
        localStorage.setItem(LAST_FULL_CHECK_KEY, String(Date.now()));
      } catch {}
      announceNewChapters(newChapterManga);
    } finally {
      syncRunningRef.current = false;
      setSyncStatus({ phase: 'idle', lastSyncAt: Date.now(), progress: null });
    }
  }, [applyCheckedManga]);

  // Realtime poll: read the sites' latest-update feeds and directly check only the manga they mention
  const runRealtimePoll = useCallback(async () => {
    if (syncRunningRef.current || document.visibilityState !== 'visible') return;
    let lastFull = 0;
    try {
      lastFull = Number(localStorage.getItem(LAST_FULL_CHECK_KEY)) || 0;
    } catch {}
    if (Date.now() - lastFull > FULL_CHECK_INTERVAL_MS) {
      await runFullCheck();
      return;
    }

    syncRunningRef.current = true;
    setSyncStatus((s) => ({ ...s, phase: 'polling' }));
    try {
      const ids = new Set(await findMangaWithFeedUpdates(mangaListRef.current));
      const targets = mangaListRef.current.filter((m) => ids.has(m.id));
      const results = await Promise.all(
        targets.map((m) =>
          checkMangaSources(m, undefined, { direct: true })
            .then((r) => ({ ...r, base: m }))
            .catch(() => null)
        )
      );
      const changed = results.filter((r) => r?.hasChanges).map((r) => ({ base: r!.base, updated: r!.updatedManga }));
      if (changed.length > 0) applyCheckedManga(changed, true);
      announceNewChapters(results.filter((r) => r?.hasUpdate).map((r) => r!.updatedManga));
    } finally {
      syncRunningRef.current = false;
      setSyncStatus({ phase: 'idle', lastSyncAt: Date.now(), progress: null });
    }
  }, [applyCheckedManga, runFullCheck]);

  // Drive the realtime loop: first sync shortly after load, then every few minutes while the tab is
  // visible, plus an immediate catch-up when returning to the tab.
  const runRealtimePollRef = useRef(runRealtimePoll);
  runRealtimePollRef.current = runRealtimePoll;
  const lastSyncAtRef = useRef<number | null>(null);
  lastSyncAtRef.current = syncStatus.lastSyncAt;
  useEffect(() => {
    if (isLoading) return;
    const tick = () => runRealtimePollRef.current().catch(() => {});
    const firstRun = setTimeout(tick, 1500);
    const interval = setInterval(tick, REALTIME_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const last = lastSyncAtRef.current || 0;
      if (Date.now() - last > REALTIME_POLL_MS) tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(firstRun);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isLoading]);

  // Background follow-up for manga added from the Discover page: fill in the sites it wasn't found
  // on, then read every site's page once for exact chapters and release times.
  const enrichAddedManga = async (manga: Manga) => {
    try {
      const knownSitesList = sites.length > 0 ? sites : getKnownSites();
      const { updatedManga: withSources } = await autoDiscoverMangaSources(manga, knownSitesList);
      const { updatedManga } = await checkMangaSources(withSources, undefined, { direct: true });
      applyCheckedManga([{ base: manga, updated: updatedManga }], true);
    } catch {}
  };

  // Add one or many manga from the Discover page at the end of their tiers in a single update
  const handleAddFromDiscover = (newOnes: Manga[]) => {
    if (newOnes.length === 0) return;
    setMangaList((prev) => {
      const result: Manga[] = [];
      for (const g of TIER_ORDER) {
        const items = prev.filter((m) => m.grade === g).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        items.push(...newOnes.filter((m) => m.grade === g));
        result.push(...items);
      }
      let globalOrder = 1;
      const finalOrderedList = result.map((m) => withOrder(m, globalOrder++));
      saveLocalManga(finalOrderedList);
      return finalOrderedList;
    });
    newOnes.forEach((m) => unrecordDeletedMangaId(m.id));
    showToast(
      newOnes.length === 1
        ? `เพิ่มมังงะเรื่องใหม่ "${newOnes[0].title}" สำเร็จ!`
        : `เพิ่มมังงะใหม่ ${newOnes.length} เรื่องเข้า Tier ${newOnes[0].grade} สำเร็จ!`
    );

    // Fill in the other sites and release times a few at a time (1668manga lookups are rate limited)
    const queue = [...newOnes];
    const worker = async () => {
      while (queue.length > 0) await enrichAddedManga(queue.shift() as Manga);
    };
    for (let i = 0; i < Math.min(3, queue.length); i++) worker();
  };

  // Batch Auto-Discover for ALL manga: High-speed Concurrent Worker Pool using unified autoDiscoverMangaSources
  const handleAutoDiscoverAll = async () => {
    if (isAutoDiscoveringAll || syncRunningRef.current) return;

    const workingList = [...mangaList];
    if (workingList.length === 0) {
      showToast('ไม่มีมังงะในระบบให้ค้นหา', 'info');
      return;
    }

    setIsAutoDiscoveringAll(true);
    // Holds the realtime lock so a background check and this never work on the same manga at once
    syncRunningRef.current = true;
    abortAutoDiscoverRef.current = false;
    const knownSitesList = sites.length > 0 ? sites : getKnownSites();
    const startTime = Date.now();
    let totalDiscoveredSources = 0;
    let updatedMangaCount = 0;
    const CONCURRENCY = 15; // High-speed parallel worker pool (sub-3-5s total)

    setAutoDiscoverProgress({
      current: 0,
      total: workingList.length,
      title: 'เริ่มเตรียมค้นหาเว็บ...',
      foundCount: 0,
      remainingSeconds: Math.max(1, Math.round((workingList.length / CONCURRENCY) * 0.3)),
    });

    // Found sources are merged per manga like checker results, so edits made meanwhile survive
    let pending: CheckedManga[] = [];
    try {
      let nextIndex = 0;
      let completedCount = 0;

      const workers = Array.from({ length: Math.min(CONCURRENCY, workingList.length) }, async () => {
        while (nextIndex < workingList.length) {
          if (abortAutoDiscoverRef.current) break;
          const queued = workingList[nextIndex++];
          // Search with the latest version, so edits made since the run started are what gets extended
          const manga = mangaListRef.current.find((m) => m.id === queued.id);
          if (!manga) {
            completedCount++;
            continue;
          }

          // Ultra-fast skip: if manga already has all known sites, bypass immediately (0ms)
          const hasMissing = knownSitesList.some((site) => {
            const siteHost = getSourceName(site.url).toLowerCase();
            return !manga.sources?.some(
              (s) => getSourceName(s.url).toLowerCase() === siteHost
            );
          });

          if (!hasMissing) {
            completedCount++;
            continue;
          }

          try {
            const { updatedManga, discoveredCount } = await autoDiscoverMangaSources(
              manga,
              knownSitesList
            );
            if (discoveredCount > 0) {
              totalDiscoveredSources += discoveredCount;
              updatedMangaCount++;
              pending.push({ base: manga, updated: updatedManga });
            }
          } catch {
            // Ignore single manga error
          }

          completedCount++;
          // Batched to prevent UI rendering churn; Cloudflare is synced once at the end
          if (pending.length >= 4) {
            applyCheckedManga(pending, false);
            pending = [];
          }

          const elapsedMs = Date.now() - startTime;
          const avgMsPerItem = elapsedMs / Math.max(1, completedCount);
          const remainingItems = workingList.length - completedCount;
          const remainingSec = Math.max(
            0,
            Math.round((avgMsPerItem * remainingItems) / CONCURRENCY / 1000)
          );

          setAutoDiscoverProgress({
            current: completedCount,
            total: workingList.length,
            title: manga.title,
            foundCount: totalDiscoveredSources,
            remainingSeconds: remainingSec > 0 ? remainingSec : 1,
          });
        }
      });

      await Promise.all(workers);

      // Keep what was found even when cancelled, and sync it all to Cloudflare in one go
      if (updatedMangaCount > 0) applyCheckedManga(pending, true);

      if (!abortAutoDiscoverRef.current) {
        const totalDurationSec = Math.round((Date.now() - startTime) / 1000);
        if (totalDiscoveredSources > 0) {
          showToast(
            `🎉 ค้นหาเว็บเสร็จสิ้นใน ${totalDurationSec} วินาที! พบลิงก์อ่านสำรองใหม่ ${totalDiscoveredSources} เว็บ ใน ${updatedMangaCount} เรื่อง`,
            'success'
          );
        } else {
          showToast(
            `ค้นหาเสร็จสิ้นใน ${totalDurationSec} วินาที! ทุกเรื่องมีลิงก์แหล่งอ่านครบถ้วนแล้ว ✨`,
            'info'
          );
        }
      }
    } catch (err: any) {
      showToast(`เกิดข้อผิดพลาดในการค้นหา: ${err.message}`, 'error');
    } finally {
      syncRunningRef.current = false;
      setIsAutoDiscoveringAll(false);
      setAutoDiscoverProgress(null);
    }
  };

  const updatedTodayCount = mangaList.filter(isUpdatedToday).length;

  return (
    <div className="min-h-screen bg-canvas text-zinc-100 flex flex-col font-sans select-none">
      {/* Toast Notification Banner */}
      {toastMessage && (
        <div
          className="fixed left-3 right-3 sm:left-auto sm:right-6 z-50 flex justify-center sm:justify-end pointer-events-none animate-toast-in"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 4.25rem)' }}
        >
          <div className="max-w-md px-3.5 py-2.5 rounded-xl bg-zinc-900/95 backdrop-blur-xl ring-1 ring-white/10 shadow-2xl shadow-black/50 text-xs sm:text-sm text-zinc-100 flex items-center gap-2.5 pointer-events-auto">
            {toastMessage.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : toastMessage.type === 'error' ? (
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
            ) : (
              <Info className="w-4 h-4 text-zinc-400 shrink-0" />
            )}
            <span>{toastMessage.text}</span>
          </div>
        </div>
      )}

      {/* Auto-Discover All Manga Floating Progress Banner */}
      {autoDiscoverProgress && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-50 w-full max-w-md px-3 animate-toast-in"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 4.25rem)' }}
        >
          <div className="bg-zinc-900/95 ring-1 ring-white/10 rounded-xl p-3.5 shadow-2xl shadow-black/50 backdrop-blur-xl flex flex-col gap-2.5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-zinc-100 flex items-center gap-2 truncate">
                <Loader2 className="w-3.5 h-3.5 text-zinc-400 animate-spin shrink-0" />
                <span className="truncate">กำลังค้นหา: {autoDiscoverProgress.title}</span>
              </span>
              <span className="text-zinc-500 whitespace-nowrap ml-2 tabular-nums">
                {autoDiscoverProgress.current} / {autoDiscoverProgress.total}
              </span>
            </div>

            <div className="w-full h-1 bg-white/[0.06] rounded-full overflow-hidden">
              <div
                className="h-full bg-violet-400 rounded-full transition-all duration-300"
                style={{
                  width: `${(autoDiscoverProgress.current / autoDiscoverProgress.total) * 100}%`,
                }}
              />
            </div>

            <div className="flex items-center justify-between text-[11px]">
              <div className="flex items-center gap-3 text-zinc-400">
                <span>
                  ลิงก์สำรองใหม่ <span className="text-emerald-400 font-medium">{autoDiscoverProgress.foundCount}</span> เว็บ
                </span>
                {autoDiscoverProgress.remainingSeconds !== null && (
                  <span className="tabular-nums">
                    เหลืออีก ~{autoDiscoverProgress.remainingSeconds < 60
                      ? `${autoDiscoverProgress.remainingSeconds} วิ`
                      : `${Math.floor(autoDiscoverProgress.remainingSeconds / 60)} น. ${autoDiscoverProgress.remainingSeconds % 60} วิ`}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  abortAutoDiscoverRef.current = true;
                }}
                className="text-zinc-300 hover:text-white font-medium active:scale-95"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Header */}
      <Header
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        showSearchBar={showSearchBar}
        onToggleSearchBar={() => {
          if (showSearchBar) {
            setShowSearchBar(false);
            setSearchQuery('');
          } else {
            setShowSearchBar(true);
          }
        }}
        isSortMode={isSortMode}
        onToggleSortMode={() => setIsSortMode(!isSortMode)}
        onOpenAddModal={() => {
          setEditingManga(null);
          setIsMangaModalOpen(true);
        }}
        onOpenUpdatedToday={() => setIsUpdatedTodayOpen(true)}
        onOpenSiteManager={() => setIsSiteManagerOpen(true)}
        onOpenDownloadLibrary={() => setIsDownloadLibraryOpen(true)}
        syncStatus={syncStatus}
        onSyncNow={() => runFullCheck().catch(() => {})}
        view={view}
        onChangeView={setView}
        updatedTodayCount={updatedTodayCount}
        onAutoDiscoverAll={handleAutoDiscoverAll}
        isAutoDiscoveringAll={isAutoDiscoveringAll}
      />

      {/* Loading state */}
      {isLoading ? (
        <div className="flex-1 flex flex-col items-center justify-center py-24 gap-3 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          <p className="text-sm">กำลังเตรียมข้อมูลมังงะ...</p>
        </div>
      ) : view === 'discover' ? (
        <DiscoverPage mangaList={mangaList} onAddManga={handleAddFromDiscover} />
      ) : (
        /* Main Tier Board */
        <TierBoard
          mangaList={mangaList}
          searchQuery={searchQuery}
          isSortMode={isSortMode}
          onQuickPlus={handleQuickPlus}
          onQuickMinus={handleQuickMinus}
          onEdit={handleEdit}
          onOpenSourceDetails={(m) => setSourceDetailsManga(m)}
          onReorder={handleReorder}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          onMoveToTier={handleMoveToTier}
          onStartSortMode={() => setIsSortMode(true)}
        />
      )}

      {/* Floating Done Button when in Sort Mode */}
      {isSortMode && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-50 animate-toast-in"
          style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <button
            type="button"
            onClick={() => setIsSortMode(false)}
            className="flex items-center gap-2 h-11 px-5 bg-zinc-50 hover:bg-white active:scale-95 text-zinc-900 font-medium text-sm rounded-full shadow-2xl shadow-black/60 transition-all cursor-pointer"
          >
            <Check className="w-4 h-4" />
            <span>เสร็จสิ้นการจัดเรียง</span>
          </button>
        </div>
      )}

      {/* Mobile Bottom Navigation Bar (Sticky Thumb-friendly) */}
      <MobileBottomNav
        onOpenDownloadLibrary={() => setIsDownloadLibraryOpen(true)}
        onOpenAddModal={() => {
          setEditingManga(null);
          setIsMangaModalOpen(true);
        }}
        onOpenUpdatedToday={() => setIsUpdatedTodayOpen(true)}
        updatedTodayCount={updatedTodayCount}
        view={view}
        onChangeView={setView}
      />

      {/* Modals */}
      <MangaModal
        isOpen={isMangaModalOpen}
        onClose={() => {
          setIsMangaModalOpen(false);
          setEditingManga(null);
        }}
        onSave={handleSaveManga}
        onDelete={handleDeleteManga}
        initialData={editingManga}
        knownSites={sites}
      />

      <UpdatedTodayModal
        isOpen={isUpdatedTodayOpen}
        onClose={() => setIsUpdatedTodayOpen(false)}
        mangaList={mangaList}
        onQuickPlus={handleQuickPlus}
        onQuickMinus={handleQuickMinus}
        onEdit={handleEdit}
        onOpenSourceDetails={(m) => setSourceDetailsManga(m)}
      />

      <DownloadLibraryModal
        isOpen={isDownloadLibraryOpen}
        onClose={() => setIsDownloadLibraryOpen(false)}
        mangaList={mangaList}
        onUpdateManga={handleSaveManga}
      />

      <SiteManagerModal
        isOpen={isSiteManagerOpen}
        onClose={() => setIsSiteManagerOpen(false)}
        sites={sites}
        onSaveSites={handleSaveSites}
        onResetAllManga={handleResetToDefault}
        backupInfo={backupInfo}
        onSaveBackup={handleSaveBackup}
        onRestoreBackup={handleRestoreBackup}
        onExportBackup={handleExportBackup}
        onImportBackup={handleImportBackup}
      />

      <SourceDetailsModal
        isOpen={Boolean(sourceDetailsManga)}
        onClose={() => setSourceDetailsManga(null)}
        manga={
          sourceDetailsManga
            ? mangaList.find((m) => m.id === sourceDetailsManga.id) || sourceDetailsManga
            : null
        }
        onUpdateManga={(updated) => {
          handleSaveManga(updated);
          setSourceDetailsManga(updated);
        }}
        knownSites={sites}
      />
    </div>
  );
};

export default App;
